import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import PrettyNum from '../utils/pretty-num-wrapper';
import { alias } from '@ember/object/computed';
import Utils from '../utils/utils';
import cloneDeep from 'lodash.clonedeep';

export default class SigmaDeltaDataService extends Service {
    @service intl;
    @service selectionData;
    @service hashService;
    @service partData;

    @alias('selectionData.selectedAdc') selectedAdc;
    @alias('selectionData.selectedAdc.partCore') adcPartCore;
    @alias('selectionData.selectedDriver.partCore') driverPartCore;

    isSigmaDeltaAdc = false;

    init() {
        super.init();
        this.set('initialLoad', true);
    }

    /**
     * Assign initial values for user input sigma delta properties on
     * application load and whenever the selected adc is changed
     *
     * Override default values with the hash values if found in the url
     */
    refreshSigmaDeltaDefaults() {
        let selectedAdc = this.selectedAdc,
            inputs = this.partData.sigmaDeltaInputs[this.selectedAdc.partCore],
            isSigmaDeltaAdc = selectedAdc.deviceArchitecture === 'sigma-delta' && (inputs ? true : false);

        this.set('currentAdcInputs', inputs);

        if (this.allowMclkInput) this.set('mclk', isSigmaDeltaAdc ? inputs.mclk.default : null);
        else this.set('mclk', null);

        if (this.allowOdrInput) this.set('odrInput', isSigmaDeltaAdc ? inputs.odr.default : null);
        else this.set('odrInput', null);

        if (this.allowMclkDivInput) this.setOptionFromDefault('mclkDiv', isSigmaDeltaAdc);
        else this.set('mclkDiv', null);

        this.setOptionFromDefault('powerMode', isSigmaDeltaAdc);
        this.setOptionFromDefault('filterType', isSigmaDeltaAdc);

        if (this.allowOdrInput) this.setOptionFromDefault('channelAveraging', isSigmaDeltaAdc);
        else this.set('channelAveraging', null);

        //let decimationRateId = isSigmaDeltaAdc ? inputs.decimationRate[this.filterType.id].default : "";
        if (this.allowDecimationRateInput)
            this.setOptionFromDefault('decimationRate', isSigmaDeltaAdc, this.filterType ? this.filterType.id : null);
        else this.set('decimationRate', null);

        // The hash values will override the defaults if found
        if (isSigmaDeltaAdc && this.initialLoad) {
            let hashValues = this.getHashValues();
            Object.keys(hashValues).forEach((propertyName) => this.set(propertyName, hashValues[propertyName]));
        }

        this.set('initialLoad', false);
        this.set('isSigmaDeltaAdc', isSigmaDeltaAdc);
    }

    /**
     * Sigma delta computed values
     * These may change based on user user input sigma delta properties
     */

    @computed('currentAdcInputs.mclk') get allowMclkInput() {
        if (!this.currentAdcInputs) return false;
        return !!this.currentAdcInputs.mclk;
    }

    @computed('currentAdcInputs.mclkDiv') get allowMclkDivInput() {
        if (!this.currentAdcInputs) return false;
        return !!this.currentAdcInputs.mclkDiv;
    }

    @computed('allowMclkInput', 'allowMclkDivInput') get showFmod() {
        return this.allowMclkInput && this.allowMclkDivInput;
    }

    @computed('currentAdcInputs.odr') get allowOdrInput() {
        if (!this.currentAdcInputs) return false;
        return !!this.currentAdcInputs.odr;
    }

    @computed('currentAdcInputs.averaging') get allowChannelAveragingInput() {
        if (!this.currentAdcInputs) return false;
        return !!this.currentAdcInputs.channelAveraging;
    }

    @computed('currentAdcInputs.decimationRate') get allowDecimationRateInput() {
        if (!this.currentAdcInputs) return false;
        return !!this.currentAdcInputs.decimationRate;
    }

    @computed('mclk', 'allowMclkInput') get mclkError() {
        if (!this.allowMclkInput) return '';

        let intl = this.intl,
            mclk = this.mclk,
            inputs = this.currentAdcInputs;

        if (!mclk || !inputs) return '';

        let minMclk = inputs.mclk.min,
            maxMclk = inputs.mclk.max,
            isError = mclk < minMclk || mclk > maxMclk;

        return isError
            ? intl.t('messages.mclk', { minMclk: PrettyNum.eng4(minMclk), maxMclk: PrettyNum.eng3(maxMclk) })
            : '';
    }

    @computed('mclk', 'mclkDiv') get fmod() {
        let mclk = this.mclk;
        let mclkDiv = this.mclkDiv;

        if (!Utils.isNumeric(mclk) || !mclkDiv) return null;

        return mclk / mclkDiv.value;
    }

    @computed('fmod', 'powerMode') get fmodError() {
        let intl = this.intl,
            fmod = this.fmod,
            powerMode = this.powerMode;

        if (!Utils.isNumeric(fmod) || !powerMode) return '';

        let minFmod = powerMode.fmod.min,
            maxFmod = powerMode.fmod.max,
            isMinError = fmod < minFmod,
            isMaxError = fmod > maxFmod,
            message = '';

        if (isMinError) {
            message = intl.t('messages.fmodMin', { powerMode: powerMode.id, minFmod: PrettyNum.eng3(minFmod) });
        } else if (isMaxError) {
            message = intl.t('messages.fmodMax', { powerMode: powerMode.id, maxFmod: PrettyNum.eng3(maxFmod) });
        }

        return message;
    }

    @computed('filterType.odrRange.{min,max}') get odrRange() {
        return this.filterType && this.filterType.odrRange ? this.filterType.odrRange : null;
    }

    @computed('odrInput', 'filterType.value', 'odrRange.{min,max}') get odrError() {
        let odrValue = this.odrInput,
            filterType = this.filterType,
            odrRange = this.odrRange,
            message = '';

        if (!Utils.isNumeric(odrValue) || !odrRange) return '';

        if (odrValue < odrRange.min || odrValue > odrRange.max) {
            message = this.intl.t('messages.odr_out_of_range', {
                filter: filterType.intlKey
                    ? this.intl.t(`sigma_delta_data.filter_type.${filterType.intlKey}`)
                    : filterType.value,
                min: PrettyNum.eng4(odrRange.min),
                max: PrettyNum.eng4(odrRange.max),
            });
        }

        return message;
    }

    /**
     * Sigma delta adc's sampling rate is dependent on fmod
     * Override the sampling rate in the model when the fmod changes
     */
    getFsample() {
        let fmod = this.fmod;

        if (this.isSigmaDeltaAdc && Utils.isNumeric(this.odrInput)) {
            return this.odrInput;
        } else if (this.isSigmaDeltaAdc && Utils.isNumeric(fmod)) {
            return fmod * 2;
        } else {
            return null;
        }
    }

    getRCValues() {
        let isSigmaDeltaAdc = this.isSigmaDeltaAdc,
            driverPartCore = this.driverPartCore,
            powerMode = this.powerMode;

        if (
            !(
                isSigmaDeltaAdc &&
                driverPartCore &&
                powerMode &&
                powerMode.rcDefaults &&
                driverPartCore in powerMode.rcDefaults
            )
        ) {
            return null;
        }

        return powerMode.rcDefaults[driverPartCore];
    }

    @computed(
        'currentAdcInputs.noiseCalculationModel',
        'filterType.libraryValue',
        'odrInput',
        'channelAveraging.libraryValue',
        'powerMode.libraryValue',
    )
    get adcNoiseCalculationModel() {
        if (
            !(
                this.currentAdcInputs &&
                this.filterType &&
                this.channelAveraging &&
                this.powerMode &&
                Utils.isNumeric(this.odrInput)
            )
        )
            return null;
        return this.currentAdcInputs.noiseCalculationModel
            ? {
                  userConfig: {
                      filterProfile: this.filterType.libraryValue,
                      outputDataRate: this.odrInput,
                      averaging: this.channelAveraging.libraryValue,
                      powerMode: this.powerMode.libraryValue,
                  },
              }
            : null;
    }

    @computed('fmod', 'decimationRate', 'odrInput') get odr() {
        let fmod = this.fmod,
            decimationRate = this.decimationRate ? this.decimationRate.value : null;

        if (Utils.isNumeric(this.odrInput)) return this.odrInput;
        if (!Utils.isNumeric(fmod) || !Utils.isNumeric(decimationRate)) return null;

        return fmod / decimationRate;
    }

    @computed('filterType', 'odr') get adcEquivNoiseBW() {
        let filterType = this.filterType,
            odr = this.odr;

        if (!filterType || !odr) return null;

        return odr * filterType.adcEquivNoiseBWMultiplier;
    }

    @computed('powerMode') get adcNoiseDensity() {
        let powerMode = this.powerMode;
        if (!powerMode) return null;
        return powerMode.adcNoiseDensity;
    }

    /**
     * The options for each sigma delta value.
     * These options will change depending on the selected adc
     */

    @computed('currentAdcInputs') get mclkDivOptions() {
        let inputs = this.currentAdcInputs;
        if (!inputs) return [];
        return inputs.mclkDiv.values.map((value) => this.getMclkDivOption(value));
    }

    @computed('currentAdcInputs') get powerModeOptions() {
        let inputs = this.currentAdcInputs;
        if (!inputs) return [];
        return inputs.powerMode.items.map((item) => this.getGenericOptionFromItem(item, 'power_mode'));
    }

    @computed('currentAdcInputs') get filterTypeOptions() {
        let inputs = this.currentAdcInputs;
        if (!inputs) return [];
        return inputs.filterType.items.map((item) => this.getGenericOptionFromItem(item, 'filter_type'));
    }

    @computed('currentAdcInputs') get channelAveragingOptions() {
        let inputs = this.currentAdcInputs;
        if (!inputs) return [];
        return inputs.channelAveraging.items.map((item) => this.getGenericOptionFromItem(item, 'channel_averaging'));
    }

    @computed('currentAdcInputs', 'filterType') get decimationRateOptions() {
        let filterType = this.filterType,
            decimationRateCombinedOptions = this.decimationRateCombinedOptions;

        if (!filterType || !decimationRateCombinedOptions) return [];

        return filterType.id in decimationRateCombinedOptions ? decimationRateCombinedOptions[filterType.id] : [];
    }

    @computed('currentAdcInputs') get decimationRateCombinedOptions() {
        let inputs = this.currentAdcInputs;
        let result = {};

        if (!(inputs && inputs.decimationRate)) return [];

        let decimationRateInput = inputs.decimationRate;

        Object.keys(decimationRateInput).forEach((filterTypeName) => {
            let option = decimationRateInput[filterTypeName];
            result[filterTypeName] = option.values.map((value) => this.getGenericOption(value));
        });

        return result;
    }

    /**
     * When the filter type changes, we need to update the decimation rate options / selected value
     */
    setFilterTypeProperties() {
        let isSigmaDeltaAdc = this.isSigmaDeltaAdc;
        let filterType = this.filterType;

        if (!filterType || !isSigmaDeltaAdc || !this.allowDecimationRateInput) {
            this.set('decimationRate', null);
            return;
        }
        this.setOptionFromDefault('decimationRate', isSigmaDeltaAdc, filterType.id);
    }

    /**
     * Functions for getting an option from a list of options
     */

    setOptionFromDefault(propertyName, isSigmaDeltaAdc, otherKey) {
        if (!isSigmaDeltaAdc) {
            this.set(propertyName, null);
            return;
        }

        let options = otherKey
                ? this.get(`${propertyName}CombinedOptions`)[otherKey]
                : this.get(`${propertyName}Options`),
            inputs = otherKey ? this.currentAdcInputs[propertyName][otherKey] : this.currentAdcInputs[propertyName];

        this.set(
            propertyName,
            options.find((option) => option.id === inputs.default),
        );
    }

    getOptionFromId(propertyName, id, otherKey) {
        let options = otherKey
            ? this.get(`${propertyName}CombinedOptions`)[otherKey]
            : this.get(`${propertyName}Options`);
        return options.find((option) => option.id === id);
    }

    /**
     * Functions for getting sigma delta values from the url / hash
     */

    getHashValues() {
        let values = {};

        if (this.allowOdrInput) this.setValueFromHash('odrInput', values);
        if (this.allowMclkDivInput) this.setValueFromHash('mclk', values);
        if (this.allowMclkDivInput) this.setSigmaDeltaOptionFromHash('mclkDiv', values);
        this.setSigmaDeltaOptionFromHash('powerMode', values);
        this.setSigmaDeltaOptionFromHash('filterType', values);

        if (this.allowChannelAveragingInput) this.setSigmaDeltaOptionFromHash('channelAveraging', values);

        let filterType = values.filterType || this.filterType;
        if (filterType && this.allowDecimationRateInput)
            this.setSigmaDeltaOptionFromHash('decimationRate', values, filterType.id);

        return values;
    }

    setValueFromHash(propertyName, values) {
        let hashValues = this.hashService.parsedLocationHash,
            hashValue = hashValues[propertyName],
            value = Utils.convertDataTypeFromHash(hashValue);

        if (value) {
            values[propertyName] = value;
        }
    }

    setSigmaDeltaOptionFromHash(propertyName, values, otherKey) {
        let hashValues = this.hashService.parsedLocationHash,
            hashValue = hashValues[propertyName],
            value = Utils.convertDataTypeFromHash(hashValue);

        if (value) {
            let option = this.getOptionFromId(propertyName, value, otherKey);
            if (option) {
                values[propertyName] = option;
            }
        }
    }

    /**
     * Functions for converting values to options
     */

    getMclkDivOption(value) {
        return {
            id: value,
            value: value,
            description: `/ ${value}`,
        };
    }

    getGenericOption(value) {
        return {
            id: value,
            value: value,
            description: value,
        };
    }

    getGenericOptionFromItem(item, type) {
        let result = cloneDeep(item);
        result.id = item.value;
        result.description = item.intlKey ? this.intl.t(`sigma_delta_data.${type}.${item.intlKey}`) : item.value;
        return result;
    }
}
